import { useCallback, useEffect, useRef, useState } from 'react'
import { AddressForm } from './components/AddressForm'
import { AdminPage } from './components/AdminPage'
import { BrandMark } from './components/BrandMark'
import { DocHydrationBanner } from './components/DocHydrationBanner'
import { LoadingProgress } from './components/LoadingProgress'
import { Report } from './components/Report'
import { ReportByIdPage } from './components/ReportByIdPage'
import { SiteGate } from './components/SiteGate'
import { runFeasibility, type FeasibilityReport, type FeasibilityRequest, type PendingDoc } from './api'
import { applyPalette, clearPalette, loadPaletteId } from './palettes'

export default function App() {
  // Lightweight path-based routing — FastAPI's SPA catch-all serves
  // index.html for any non-/api path, so we just look at the pathname.
  const path = window.location.pathname

  // /r/<id> — public report page, shared with clients + read by the
  // Moya Copilot. Rendered BEFORE the password gate so shared links
  // open without a password.
  if (path.startsWith('/r/')) {
    const reportId = path.slice('/r/'.length).split('/')[0]
    if (reportId) {
      return <ReportByIdPage reportId={reportId} />
    }
  }

  // Everything else (landing, feasibility form, admin) is behind the
  // shared-password site gate.
  return (
    <SiteGate>
      {path.startsWith('/admin') ? <AdminPage /> : <FeasibilityApp />}
    </SiteGate>
  )
}

// Versioned key so we can change the persisted shape without parsing
// stale rows from a previous deploy. Bump the suffix on shape changes.
const SESSION_KEY = 'massing_session_v3'
// Sessions older than this expire automatically — a forgotten tab from
// last week shouldn't trigger any UI on a fresh load.
const SESSION_TTL_MS = 30 * 60 * 1000  // 30 min

interface PersistedSession {
  req: FeasibilityRequest
  pendingDocs: PendingDoc[]
  savedAt: number  // epoch ms
}

function loadSession(): PersistedSession | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY)
    if (!raw) return null
    const s = JSON.parse(raw) as PersistedSession
    if (!s || !s.req || !s.req.address) return null
    if (typeof s.savedAt !== 'number' || Date.now() - s.savedAt > SESSION_TTL_MS) {
      // Stale — clear it so the next mount sees no session at all.
      localStorage.removeItem(SESSION_KEY)
      return null
    }
    return {
      req: s.req,
      pendingDocs: Array.isArray(s.pendingDocs) ? s.pendingDocs : [],
      savedAt: s.savedAt,
    }
  } catch {
    return null
  }
}

function saveSession(s: Omit<PersistedSession, 'savedAt'> | null) {
  try {
    if (s) localStorage.setItem(SESSION_KEY, JSON.stringify({ ...s, savedAt: Date.now() }))
    else localStorage.removeItem(SESSION_KEY)
  } catch {
    /* quota / private mode — non-fatal */
  }
}

function FeasibilityApp() {
  const [report, setReport] = useState<FeasibilityReport | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Pending docs are the cert / contract jobs the AddressForm kicked
  // off in the background. We display their parse status above the
  // report and silently re-fetch the feasibility once they all
  // settle, so Section B overlays + the CDC verdict reflect the
  // council-issued findings without the user having to refresh.
  const [pendingDocs, setPendingDocs] = useState<PendingDoc[]>([])
  // Flipped TRUE the instant every doc in pendingDocs reaches a
  // terminal state. PDF gating reads this so the button enables the
  // moment review finishes, even though the banner stays mounted for
  // a few more seconds to show a clear "reviewed" success state.
  const [docsAllSettled, setDocsAllSettled] = useState(false)
  const [hydrating, setHydrating] = useState(false)
  // Keep the last request around for the silent re-fetch on hydrate.
  const lastReqRef = useRef<FeasibilityRequest | null>(null)
  // Persist {req, pendingDocs} to localStorage so an accidental
  // refresh / network drop / Safari swipe-back doesn't lose progress.
  // We always save the latest snapshot; on mount we restore it (see
  // the resume effect below) and resume polling automatically.

  async function onSubmit(req: FeasibilityRequest, docs: PendingDoc[]) {
    // Phase 2: POST returns immediately with a report_id; the whole
    // pipeline (geocode → spatial → CDC → analyst → email) runs
    // server-side regardless of tab state. We navigate the planner
    // to /r/<id> where ReportByIdPage polls until the pipeline
    // finishes, then renders the full report. The pending docs
    // hand-off survives via localStorage so the /r/<id> page can
    // continue surfacing the cert/contract review banner.
    setLoading(true); setError(null); setReport(null); setPendingDocs(docs); setDocsAllSettled(false); setHydrating(false)
    lastReqRef.current = req
    saveSession({ req, pendingDocs: docs })
    try {
      const r = await runFeasibility(req)
      // Full-page navigation so the route-driven App.tsx mounts the
      // /r/<id> page cleanly. Loses no state (the docs handoff is in
      // localStorage; the report id is in the URL).
      window.location.assign(`/r/${r.report_id}`)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }

  // Re-run the feasibility with edited dimensions from Section A — same
  // path as onSubmit but preserves the existing pendingDocs so any
  // in-flight cert / contract review carries over. Used by the inline
  // "Update dimensions" editor on the property summary.
  async function onRerun(overrides: Partial<FeasibilityRequest>) {
    const base = lastReqRef.current
    if (!base) return
    const req: FeasibilityRequest = { ...base, ...overrides }
    setLoading(true); setError(null); setReport(null)
    lastReqRef.current = req
    saveSession({ req, pendingDocs })
    try {
      const r = await runFeasibility(req)
      // Phase 2 — every submit creates a fresh report id;
      // navigate to /r/<id> so ReportByIdPage takes over polling
      // and rendering. The previous in-place update on /
      // disappeared with the redirect flow.
      window.location.assign(`/r/${r.report_id}`)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }

  const onAllDocsSettled = useCallback(async () => {
    // Mark settled IMMEDIATELY — this is what the PDF button gates on
    // (via docsPending derived below). The banner deliberately stays
    // mounted on top of that so the planner sees an explicit
    // "Documents reviewed" success state for a beat rather than
    // having the spinner blink out and leaving them wondering whether
    // anything finished.
    setDocsAllSettled(true)
    const req = lastReqRef.current
    if (!req) {
      // Banner can still self-dismiss after the success window even
      // without a re-fetch.
      window.setTimeout(() => {
        setPendingDocs([])
        setDocsAllSettled(false)
        saveSession(null)
      }, 7000)
      return
    }
    setHydrating(true)
    try {
      const r = await runFeasibility(req)
      // Phase 2 — re-run the feasibility with docs applied creates a
      // new report id; navigate so the planner sees the doc-enriched
      // version. If they were waiting on the form page, they're now
      // on /r/<new_id> with the fuller analysis.
      window.location.assign(`/r/${r.report_id}`)
    } catch {
      // swallow — the original report is still good
    } finally {
      setHydrating(false)
      // Hold the banner's success state for a short window so the
      // change is unmissable, THEN dismiss the banner and clear the
      // persisted session. PDF was already enabled the moment
      // docsAllSettled flipped true; nothing here blocks it.
      window.setTimeout(() => {
        setPendingDocs([])
        setDocsAllSettled(false)
        saveSession(null)
      }, 6000)
    }
  }, [])

  function cancelInFlight() {
    // Hard stop on whatever is loading right now — escape hatch for
    // an auto-restored session the planner doesn't want. The
    // in-flight fetch will still resolve in the background, but
    // `lastReqRef.current = null` + setReport/setLoading clears below
    // means its setReport call has nothing to land on.
    lastReqRef.current = null
    setLoading(false)
    setReport(null)
    setPendingDocs([])
    setDocsAllSettled(false)
    setHydrating(false)
    setError(null)
    saveSession(null)
  }

  // Offer to resume any in-flight session from a previous tab session
  // — explicit Resume click only, NEVER auto-runs. The previous
  // behaviour re-ran the feasibility on every page refresh which was
  // both surprising and locked the form for tens of seconds.
  //
  // Snapshot the resumable session ONCE on mount so a re-render after
  // the planner dismisses it doesn't bring it back from the live
  // localStorage state we ourselves keep writing.
  const [resumable, setResumable] = useState<PersistedSession | null>(null)
  useEffect(() => {
    const s = loadSession()
    if (s && s.pendingDocs && s.pendingDocs.length > 0) {
      setResumable(s)
    } else if (s) {
      // Empty-pendingDocs session — clear silently; nothing to resume.
      saveSession(null)
    }
  }, [])

  function resumeSession() {
    if (!resumable) return
    const s = resumable
    setResumable(null)
    lastReqRef.current = s.req
    setPendingDocs(s.pendingDocs)
    setLoading(true)
    runFeasibility(s.req)
      .then((r) => {
        if (lastReqRef.current === s.req) {
          window.location.assign(`/r/${r.report_id}`)
        }
      })
      .catch((e) => {
        if (lastReqRef.current === s.req) {
          setError(e instanceof Error ? e.message : String(e))
        }
      })
      .finally(() => {
        if (lastReqRef.current === s.req) setLoading(false)
      })
  }

  function dismissResumable() {
    setResumable(null)
    saveSession(null)
  }

  // Keep the persisted session in sync with the live state. Skipped
  // until lastReqRef is populated (i.e. the user has actually
  // submitted at least once) so we don't write an empty row on the
  // very first mount.
  useEffect(() => {
    if (!lastReqRef.current) return
    // Both pendingDocs cleared and not hydrating = run is done, no
    // need to keep the session around.
    if (pendingDocs.length === 0 && !hydrating) {
      saveSession(null)
      return
    }
    saveSession({ req: lastReqRef.current, pendingDocs })
  }, [pendingDocs, hydrating])

  return (
    <div className="min-h-full">
      <header
        className="sticky top-0 z-10 backdrop-blur-md header-bg border-b border-massing-rule"
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        {/* Header lockup mirrors the in-platform spec from the brand
            system reference at desktop widths (120 px mark, 38 px
            wordmark, 14 px gap inside the lockup, 32 px gap to the
            subtitle). On mobile (< sm) we shrink the mark + wordmark
            and hide the subtitle so the lockup fits a 375 px viewport
            without horizontal overflow — previously the desktop spec
            forced the whole page out by ~60 px on iPhone. */}
        <div className="max-w-6xl mx-auto px-4 sm:px-10 h-[72px] sm:h-[117px] py-4 sm:py-7 flex items-center justify-between gap-3">
          <a href="/" className="flex items-center gap-3 sm:gap-8 no-underline min-w-0">
            <span className="flex items-center gap-2 sm:gap-[14px] shrink-0">
              <BrandMark className="w-[60px] sm:w-[120px]" />
              <span className="brand-wordmark brand-gradient-text leading-none text-[24px] sm:text-[38px]">massing</span>
            </span>
            {/* Subtitle reserved for sm+ screens — on mobile the
                lockup + admin chip already say what app you're in. */}
            <span className="brand-eyebrow text-massing-ink-light hidden sm:inline">NSW residential feasibility</span>
          </a>
          <div className="flex items-center gap-2 sm:gap-4 shrink-0">
            <ThemeToggle />
            <a
              href="/admin"
              className="brand-eyebrow text-massing-ink-light hover:text-massing-ink transition-colors no-underline"
            >
              admin
            </a>
            <span className="brand-eyebrow hidden sm:inline">v0.1</span>
          </div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-8 py-12">
        {resumable && (
          <div className="mb-4 rounded-md border border-massing-highlight/40 glass px-4 py-3 flex items-start justify-between gap-4">
            <div className="text-sm text-massing-ink">
              <span className="brand-eyebrow text-massing-ink-light mr-2">In-progress</span>
              You have an unfinished feasibility for{' '}
              <span className="font-medium">{resumable.req.address}</span>
              {resumable.pendingDocs.length > 0 && (
                <span className="text-massing-ink-light"> · {resumable.pendingDocs.length} doc{resumable.pendingDocs.length === 1 ? '' : 's'} still reviewing</span>
              )}.
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <button
                type="button"
                onClick={resumeSession}
                className="bg-massing hover:bg-massing-deep text-massing-cream text-sm px-3.5 py-1.5 rounded-sm transition-colors"
              >
                Resume
              </button>
              <button
                type="button"
                onClick={dismissResumable}
                className="text-[11px] tracking-[0.12em] uppercase text-massing-ink-light hover:text-massing-ink"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}
        <AddressForm onSubmit={onSubmit} loading={loading} />
        {error && (() => {
          // The backend tags upstream NSW spatial-portal failures with
          // a literal "NSW Planning Portal" prefix in the detail
          // message. Match on that to swap into a friendlier amber
          // panel — the planner reads "their problem, not ours" at a
          // glance and can retry without re-typing the address.
          const isPortalIssue = error.includes('NSW Planning Portal')
          const lastReq = lastReqRef.current
          return (
            <div
              className={
                'mt-4 p-4 rounded-sm border ' +
                (isPortalIssue
                  ? 'bg-flag-amber/10 border-flag-amber/40 text-massing-ink'
                  : 'bg-massing-maroon-soft border-massing-rule text-massing-maroon')
              }
            >
              {isPortalIssue && (
                <div className="brand-eyebrow text-flag-amber mb-1.5">
                  NSW Planning Portal — temporarily slow
                </div>
              )}
              <div className="text-sm leading-snug">{error}</div>
              {lastReq && (
                <div className="mt-3 flex items-center gap-3">
                  <button
                    type="button"
                    disabled={loading}
                    onClick={() => onSubmit(lastReq, pendingDocs)}
                    className="bg-massing hover:bg-massing-deep disabled:opacity-50 disabled:cursor-not-allowed text-massing-cream px-4 py-1.5 text-sm tracking-wide transition-colors rounded-sm"
                  >
                    Retry feasibility
                  </button>
                  <button
                    type="button"
                    onClick={() => setError(null)}
                    className="brand-eyebrow text-massing-ink-light hover:text-massing-ink"
                  >
                    Dismiss
                  </button>
                </div>
              )}
            </div>
          )
        })()}
        {loading && <LoadingProgress pendingDocs={pendingDocs} onCancel={cancelInFlight} />}
        {report && pendingDocs.length > 0 && (
          <DocHydrationBanner pending={pendingDocs} onAllDone={onAllDocsSettled} />
        )}
        {report && hydrating && (
          <div className="mt-3 text-[12px] brand-eyebrow text-massing-ink-light italic">
            refreshing the report with the verified documents…
          </div>
        )}
        {report && <Report
          report={report}
          docsPending={pendingDocs.length > 0 && !docsAllSettled}
          certPending={pendingDocs.some((d) => d.kind === 'cert') && !docsAllSettled}
          contractPending={pendingDocs.some((d) => d.kind === 'contract') && !docsAllSettled}
          onRerun={onRerun}
        />}
      </main>
    </div>
  )
}

// Persistent dark / light theme switch. The DOM attribute applies
// instantly via CSS variable cascade (see index.css). Default is dark
// because the platform's identity is the aurora theme; light mode is a
// brighter option for daytime / projector / accessibility use.
const THEME_KEY = 'massing_theme'
type Theme = 'dark' | 'light'

function readTheme(): Theme {
  try {
    const v = localStorage.getItem(THEME_KEY)
    if (v === 'light' || v === 'dark') return v
  } catch { /* ignore */ }
  return 'dark'
}

function ThemeToggle() {
  const [theme, setTheme] = useState<Theme>(() => readTheme())
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme)
    // The palette's CSS variables (--bg-deep, --aurora-*, --grad-bg-*)
    // are set as INLINE STYLES on <html>, which beats every CSS
    // selector by specificity. In light mode those dark-tuned values
    // would override our [data-theme="light"] rules. So: drop them
    // when going light, reapply on the way back to dark.
    if (theme === 'light') {
      clearPalette()
    } else {
      applyPalette(loadPaletteId())
    }
    try { localStorage.setItem(THEME_KEY, theme) } catch { /* ignore */ }
  }, [theme])
  const next: Theme = theme === 'dark' ? 'light' : 'dark'
  return (
    <button
      type="button"
      onClick={() => setTheme(next)}
      title={`Switch to ${next} theme`}
      aria-label={`Switch to ${next} theme`}
      className="brand-eyebrow text-massing-ink-light hover:text-massing-ink transition-colors flex items-center gap-1.5 px-2 py-1 rounded-sm"
    >
      {theme === 'dark' ? (
        <>
          <SunIcon />
          <span className="hidden sm:inline">light</span>
        </>
      ) : (
        <>
          <MoonIcon />
          <span className="hidden sm:inline">dark</span>
        </>
      )}
    </button>
  )
}

function SunIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
    </svg>
  )
}

function MoonIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  )
}
